<script setup lang="ts">
const { imageUrl } = defineProps<{
  imageUrl: string;
}>();

const emit = defineEmits<{
  (e: "click"): void;
  (e: "keydown"): void;
}>();

const TOTAL_IMAGES = 36;
const PHYSICS_SENSITIVITY = 5;

const MIN_ZOOM_SCALE = 1;
const MAX_ZOOM_SCALE = 3;
const SCALE_CONVERSION_FACTOR = 100;

const isLoading = ref(false);
const firstImageLoaded = ref(false);
const loadedImages = ref<Set<number>>(new Set());
const currentImageLoading = ref(false);
const currentImageError = ref(false);

const startedLoading = new Set<number>();

const { isTouchDevice } = useDeviceInformation();

// Physics-based dragging while keeping slider control
const {
  currentImageIndex,
  isDragging,
  hasDragged,
  isAnimating,
  startDrag,
  onDrag,
  endDrag,
  stopAnimation,
} = use360Physics(TOTAL_IMAGES, 0, PHYSICS_SENSITIVITY, isTouchDevice);

// Watch for imageUrl prop changes to reload
watch(
  () => imageUrl,
  (newImageUrl) => {
    if (newImageUrl) {
      resetState();
      nextTick(() => {
        loadFirstImage();
      });
    }
  },
  { immediate: false },
);

const currentScale = ref(1);
const firstImageLoading = computed(
  () => isLoading.value && !firstImageLoaded.value,
);

const shouldShowLoading = computed(() => {
  return firstImageLoading.value || currentImageLoading.value;
});

const shouldShowSlider = computed(() => {
  return firstImageLoaded.value && !isDragging.value && !isAnimating.value;
});

const currentImageUrl = computed(() => {
  const index = Number(currentImageIndex.value);
  const imageNumber = String(index + 1).padStart(2, "0");

  return `${imageUrl}${imageNumber}.jpg`;
});

/**
 * Reset component state
 */
const resetState = () => {
  isLoading.value = false;
  firstImageLoaded.value = false;
  loadedImages.value.clear();
  currentImageLoading.value = false;
  currentImageError.value = false;
  startedLoading.clear();
};

const loadImageWithFeedback = async (index: number) => {
  if (loadedImages.value.has(index)) {
    currentImageLoading.value = false;

    return;
  }

  try {
    const img = new Image();
    const imageNumber = String(index + 1).padStart(2, "0");

    await new Promise<void>((resolve, reject) => {
      img.onload = () => {
        loadedImages.value.add(index);
        if (index === currentImageIndex.value) {
          currentImageLoading.value = false;
          currentImageError.value = false;
        }
        resolve();
      };
      img.onerror = () => {
        if (index === currentImageIndex.value) {
          currentImageLoading.value = false;
        }
        reject(new Error(`Failed to load image ${index}`));
      };
      img.src = `${imageUrl}${imageNumber}.jpg`;
    });
  } catch {
    if (index === currentImageIndex.value) {
      currentImageLoading.value = false;
      currentImageError.value = true;
    }
  }
};

/**
 * Load initial batch of images to make component truly interactive
 */
const loadFirstImage = async () => {
  isLoading.value = true;
  const startIndex = currentImageIndex.value || 0;

  try {
    // Load initial batch: current image ± 4 positions (9 images total)
    // This gives users enough images for immediate smooth rotation
    const initialRange = 4;
    const indices = Array.from(
      { length: initialRange * 2 + 1 },
      (_, i) => (startIndex + i - initialRange + TOTAL_IMAGES) % TOTAL_IMAGES,
    );

    await Promise.allSettled(
      indices.map((index) => {
        const imageNumber = String(index + 1).padStart(2, "0");

        return new Promise<void>((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            loadedImages.value.add(index);
            resolve();
          };
          img.onerror = () =>
            reject(new Error(`Failed to load image ${index}`));
          img.src = `${imageUrl}${imageNumber}.jpg`;
        });
      }),
    );

    firstImageLoaded.value = true;
    isLoading.value = false;

    preloadRemainingImages();
  } catch {
    isLoading.value = false;
    firstImageLoaded.value = true;
    loadedImages.value.add(startIndex);
    preloadRemainingImages();
  }
};

const preloadRemainingImages = () => {
  const startIndex = currentImageIndex.value || 0;

  // Helper function to load an image and return a promise
  const loadImage = (index: number): Promise<void> => {
    if (loadedImages.value.has(index) || startedLoading.has(index)) {
      return Promise.resolve();
    }

    startedLoading.add(index);

    return new Promise<void>((resolve) => {
      const img = new Image();
      const imageNumber = String(index + 1).padStart(2, "0");

      img.onload = () => {
        loadedImages.value.add(index);
        resolve();
      };

      img.onerror = () => {
        setTimeout(() => {
          const retryImg = new Image();
          retryImg.src = `${imageUrl}${imageNumber}.jpg`;

          retryImg.onload = () => {
            loadedImages.value.add(index);
            resolve();
          };

          retryImg.onerror = () => {
            resolve();
          };
        }, 100);
      };

      img.src = `${imageUrl}${imageNumber}.jpg`;
    });
  };

  // Load remaining images in expanding waves, waiting for each wave to complete
  const loadWaves = async () => {
    let currentRadius = 3;

    while (currentRadius <= Math.floor(TOTAL_IMAGES / 2)) {
      const wavePromises: Promise<void>[] = [];

      const prevIndex =
        (startIndex - currentRadius + TOTAL_IMAGES) % TOTAL_IMAGES;
      const nextIndex = (startIndex + currentRadius) % TOTAL_IMAGES;

      wavePromises.push(loadImage(prevIndex));
      wavePromises.push(loadImage(nextIndex));

      await Promise.allSettled(wavePromises);

      currentRadius++;
    }
  };

  loadWaves();
};

const handleImageClick = () => {
  if (!hasDragged.value) {
    emit("click");
  }
};

const handleMouseDown = (event: MouseEvent) => {
  event.preventDefault();
  startDrag(event.clientX);
  document.body.style.cursor = "grabbing";
};

const handleMouseMove = (event: MouseEvent) => {
  if (!isDragging.value) {
    return;
  }

  event.preventDefault();
  onDrag(event.clientX);
};

const handleMouseUp = () => {
  if (isDragging.value) {
    endDrag();
    document!.body!.style!.cursor = "";
  }
};

const lastTouchMoveTime = ref(0);
const touchStartX = ref(0);
const touchStartY = ref(0);
const touchStartTime = ref(0);
const hasDeterminedGesture = ref(false);

const handleImageTouchStart = (event: TouchEvent) => {
  if (event.touches.length > 1) {
    return; // Ignore multi-touch events
  }

  const touch = event.touches[0];
  if (touch) {
    touchStartX.value = touch.clientX;
    touchStartY.value = touch.clientY;
    touchStartTime.value = Date.now();
    hasDeterminedGesture.value = false;
  }
};

const handleTouchMove = (event: TouchEvent) => {
  if (event.touches.length > 1) {
    return; // Ignore multi-touch events
  }

  const touch = event.touches[0];
  const now = Date.now();

  if (touch && now - lastTouchMoveTime.value > 8) {
    const deltaX = Math.abs(touch.clientX - touchStartX.value);
    const deltaY = Math.abs(touch.clientY - touchStartY.value);
    const timeDelta = now - touchStartTime.value;

    // Only start drag and prevent scrolling if this is clearly a horizontal gesture
    if (
      !hasDeterminedGesture.value &&
      (deltaX > 5 || deltaY > 5 || timeDelta > 50)
    ) {
      hasDeterminedGesture.value = true;

      // If horizontal movement is dominant, start 360° rotation and prevent scrolling
      if (deltaX > deltaY && deltaX > 5) {
        startDrag(touchStartX.value);
        event.preventDefault();
      }
    }

    // Only continue drag if we're already dragging (horizontal gesture determined)
    if (isDragging.value) {
      event.preventDefault();
      lastTouchMoveTime.value = now;
      onDrag(touch.clientX);
    }
  }
};

const handleTouchEnd = () => {
  hasDeterminedGesture.value = false;
  endDrag();
};

const handleMouseLeave = () => {
  if (isDragging.value) {
    endDrag();
  }
  document!.body!.style!.cursor = "";
};

const handleContainerKeydown = () => {
  emit("keydown");
};

onMounted(() => {
  if (!firstImageLoaded.value) {
    loadFirstImage();
  }

  // Check if current image is loaded on mount
  if (!loadedImages.value.has(currentImageIndex.value)) {
    currentImageLoading.value = true;
    loadImageWithFeedback(currentImageIndex.value);
  }
});

onUnmounted(() => {
  // Reset cursor state
  document!.body!.style!.cursor = "";
  stopAnimation();
});

watch(
  currentImageIndex,
  (newIndex) => {
    if (!loadedImages.value.has(newIndex)) {
      currentImageLoading.value = true;
      currentImageError.value = false;
      loadImageWithFeedback(newIndex);
    } else {
      currentImageLoading.value = false;
    }
  },
  { immediate: true },
);

const updateScale = (newScale?: number | string) => {
  if (!newScale) return;

  currentScale.value = Number(newScale) / SCALE_CONVERSION_FACTOR;
};
</script>

<template>
  <div
    class="base-360-viewer"
    :class="{
      'is-loading': shouldShowLoading,
      'is-dragging': isDragging,
    }"
    tabindex="0"
    role="img"
    aria-label="360° Product View. Use arrow keys or slider to rotate."
    @keydown="handleContainerKeydown"
  >
    <div
      class="base-360-viewer__image-container"
      :class="{
        'base-360-viewer__image-container--zoomed': isTouchDevice,
      }"
      data-test="360-viewer-image"
    >
      <div
        v-if="shouldShowLoading"
        class="base-360-viewer__loading-container"
      >
        <BaseLoadingSpinner />
      </div>

      <div
        v-else
        class="base-360-viewer__zoom-container"
        :class="{
          'cursor-grab': !isDragging,
          'cursor-grabbing': isDragging,
          'is-current-loading': currentImageLoading,
        }"
        data-test="360-viewer-zoom-container"
        @click="handleImageClick"
        @keydown="handleImageClick"
        @mousemove="handleMouseMove"
        @mousedown="handleMouseDown"
        @mouseup="handleMouseUp"
        @mouseleave="handleMouseLeave"
        @blur="handleMouseLeave"
      >
        <!-- Subtle loading overlay for current image -->
        <div
          v-if="currentImageLoading"
          class="base-360-viewer__current-loading-overlay"
        >
          <BaseLoadingSpinner
            class="base-360-viewer__current-loading-spinner"
          />
        </div>

        <div
          v-if="currentImageError"
          class="absolute inset-0 bg-black/40 z-30 flex flex-col items-center justify-center gap-3 text-white text-sm text-center px-4"
          role="alert"
        >
          <span>We couldn’t load this 360° image.</span>
          <BaseButton @click="loadImageWithFeedback(currentImageIndex)">
            Retry
          </BaseButton>
        </div>

        <ScrollZoomContainer
          v-model="currentScale"
          :min-scale="MIN_ZOOM_SCALE"
          :max-scale="MAX_ZOOM_SCALE"
        >
          <NuxtImg
            :src="currentImageUrl"
            alt="360° Product View - 360° view"
            class="base-360-viewer__image"
            :class="{ 'opacity-75': currentImageLoading }"
            draggable="false"
            @touchmove="handleTouchMove"
            @touchend.passive="handleTouchEnd"
            @touchstart.passive="handleImageTouchStart"
          />
        </ScrollZoomContainer>
      </div>

      <LazyProductMedia360ViewerSlider
        v-if="!shouldShowLoading && !currentImageError"
        v-model="currentImageIndex"
        :hide-slider="!shouldShowSlider"
        orient="horizontal"
        :min="0"
        :max="TOTAL_IMAGES - 1"
        class="absolute bottom-0 left-1/2 -translate-x-1/2 z-10 transition-all duration-300 ease-in-out max-w-[80%]"
      >
        <template #firstLabel>0º</template>
        <template #secondLabel>360º</template>
      </LazyProductMedia360ViewerSlider>
      <LazyProductMedia360ViewerSlider
        v-if="!shouldShowLoading && !currentImageError"
        :model-value="currentScale * SCALE_CONVERSION_FACTOR"
        :hide-slider="!shouldShowSlider"
        orient="vertical"
        :min="MIN_ZOOM_SCALE * SCALE_CONVERSION_FACTOR"
        :max="MAX_ZOOM_SCALE * SCALE_CONVERSION_FACTOR"
        class="absolute top-1/2 right-0 -translate-y-1/2 w-[54px] h-[60%] max-h-[320px] z-10 transition-all duration-300 ease-in-out"
        @update:model-value="updateScale"
      >
        <template #firstLabel>
          <BaseIcon
            name="zoomIn"
            :size="20"
          />
        </template>
        <template #secondLabel>
          <BaseIcon
            name="zoomOut"
            :size="20"
          />
        </template>
      </LazyProductMedia360ViewerSlider>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.base-360-viewer {
  @apply relative w-full overflow-hidden;

  &.is-loading {
    @apply cursor-wait;
  }

  &.is-dragging {
    @apply select-none;
  }

  &__image-container {
    @apply relative size-full flex items-center justify-center pb-0;

    &--zoomed {
      @apply pb-0;
    }
  }

  &__loading-container {
    @apply flex items-center justify-center;
    & .loading-spinner {
      @apply static;
      @apply flex-1;
    }
  }

  &__zoom-container {
    @apply w-full flex items-center justify-center overflow-hidden relative touch-pan-x touch-pan-y touch-pinch-zoom;

    &:active {
      @apply cursor-grabbing;
    }

    &.is-current-loading {
      @apply relative;
    }
  }

  &__current-loading-overlay {
    @apply absolute inset-0 bg-black/10 backdrop-blur-sm flex items-center justify-center z-20 pointer-events-none;
    transition: opacity 0.2s ease-in-out;
  }

  &__current-loading-spinner {
    @apply w-8 h-8 text-white;
  }

  &__mobile-zoom-container {
    @apply flex items-center justify-center relative overflow-visible !important;

    .base-360-viewer__image {
      @apply w-full object-contain select-none pointer-events-auto;
    }
  }

  &__image-zoom-wrapper {
    @apply flex items-center justify-center relative;
  }

  &__image {
    @apply w-full object-contain select-none pointer-events-auto;
    transform-origin: center center;
    transform: scale(1);
    transition: opacity 0.15s ease-in-out;
  }
}

@media (prefers-reduced-motion: reduce) {
  .base-360-viewer {
    &__image {
      @apply transition-none;
    }
    /* PR #32936: Respect reduced motion by removing backdrop blur on overlay */
    /* https://github.com/MatchWornShirt/monorepo/pull/32936#discussion_r2291146136 */
    &__current-loading-overlay {
      @apply backdrop-blur-0;
    }
  }
}

@media (hover: none) and (pointer: coarse) {
  .base-360-viewer {
    &__zoom-container {
      @apply cursor-auto;

      &:active {
        @apply cursor-auto;
      }
    }
  }
}
</style>
